import {
  Stack,
  Box,
  IconButton,
  Modal,
  CircularProgress,
  Typography,
  TextField,
  useTheme,
  Button,
  Divider,
  MenuItem,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import MuiAlert from '@mui/material/Alert'
import CloseIcon from '@mui/icons-material/Close'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'
import BuildIcon from '@mui/icons-material/Build'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import DashboardIcon from '@mui/icons-material/Dashboard'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useCallback, useState } from 'react'
import dayjs from 'dayjs'
import { Prescription } from '../../../types/models/Presc'
import { CustomButton } from '../../../components/molecules/CustomButton'
import { useNavigate } from 'react-router-dom'
import { useAppType } from '../../../contexts/AppTypeContext'
import { HelpContentModal } from '../../../components/organisms/HelpContentModal'
import { helpContents } from '../../../constants/helpContents'
import { useReportCallbacks } from '../../../shared'
import { useUserReportMutation } from '../../../features/prescription'
import { useAuthCheckQuery } from '../../../features/users'
import { useToast } from '../../../contexts/ToastContext'
import { currentGuidePath } from '../../../constants/guidePaths'

type Params = {
  prescription: Prescription | undefined
}

// 型定義を追加
type HelpContentKey = keyof typeof helpContents

type FormatCFormData = {
  targetItem: string
  correctValue: string
  desiredResult: string
  rececomResult: string
  webAppResult: string
  sourceType: string
  hasQr: string // 'true' | 'false' | ''
  qrDisplayed: string // 'true' | 'false' | ''
  usedManualInput: string // 'true' | 'false' | ''
  frequency: string
  dailyCount: string
}

const initialFormatCForm: FormatCFormData = {
  targetItem: '',
  correctValue: '',
  desiredResult: '',
  rececomResult: '',
  webAppResult: '',
  sourceType: '',
  hasQr: '',
  qrDisplayed: '',
  usedManualInput: '',
  frequency: '',
  dailyCount: '',
}

/**
 * 処方箋報告フック（TanStack Query Mutation 版）
 *
 * 報告成功時は invalidateQueries により処方箋リストが自動更新される。
 * onSuccess パラメータは不要になった。
 */
export const useReportPresc = ({ prescription }: Params) => {
  const { config } = useAppType()
  const [isOtherReportModalOpen, setIsOtherReportModalOpen] =
    useState<boolean>(false)
  const [isHelpModalOpen, setIsHelpModalOpen] = useState<boolean>(false)
  const theme = useTheme()
  const { showToast } = useToast()
  const [formatCModalOpen, setFormatCModalOpen] = useState<boolean>(false)
  const [selectedHelpContent, setSelectedHelpContent] =
    useState<HelpContentKey | null>(null)
  const [formatCForm, setFormatCForm] =
    useState<FormatCFormData>(initialFormatCForm)
  const navigate = useNavigate()

  // 認証情報から店舗名を取得
  const { data: authData } = useAuthCheckQuery()
  const pharmacyName =
    (authData?.data?.user as { pharmacy_name?: string })?.pharmacy_name || ''

  // 不具合報告用 Mutation
  const userReportMutation = useUserReportMutation()

  // 共通フックからレポートコールバックを取得
  const {
    reportBasicResult,
    reportRPUnit,
    reportMedication,
    reportRPIndex,
    reportSpecificError: reportSpecificErrorBase,
    isSending: isReportSending,
  } = useReportCallbacks({
    prescription,
    onReportSpecificErrorSuccess: () => {
      setIsOtherReportModalOpen(false)
      setIsHelpModalOpen(false)
      showToast(config.shortServiceName + 'への報告が完了しました。', 'success')
    },
  })

  const isSending = isReportSending || userReportMutation.isPending

  // reportSpecificError をラップ（共通フックの onSuccess で UI 更新）
  const reportSpecificError = useCallback(
    async (ocrErrorLabel: string) => {
      await reportSpecificErrorBase(ocrErrorLabel)
    },
    [reportSpecificErrorBase]
  )

  const updateFormatCForm = useCallback(
    (field: keyof FormatCFormData, value: string) => {
      setFormatCForm((prev) => {
        const next = { ...prev, [field]: value }
        // QRなしに変更時、QR表示を初期化
        if (field === 'hasQr' && value !== 'true') {
          next.qrDisplayed = ''
        }
        return next
      })
    },
    []
  )

  const isFormatCFormValid =
    formatCForm.targetItem !== '' &&
    formatCForm.correctValue.trim() !== '' &&
    formatCForm.rececomResult.trim() !== '' &&
    formatCForm.sourceType !== '' &&
    formatCForm.hasQr !== '' &&
    (formatCForm.hasQr !== 'true' || formatCForm.qrDisplayed !== '') &&
    formatCForm.usedManualInput !== '' &&
    formatCForm.frequency !== '' &&
    formatCForm.dailyCount !== ''

  const handleFormatCSubmit = async () => {
    if (!isFormatCFormValid) return

    try {
      await userReportMutation.mutateAsync({
        prescription: prescription?.id || null,
        report_data: {
          issue_detail: {
            target_item: formatCForm.targetItem,
            correct_value: formatCForm.correctValue,
            desired_result: formatCForm.desiredResult,
            rececom_result: formatCForm.rececomResult,
            web_app_result: formatCForm.webAppResult,
          },
          prescription_source: {
            source_type: formatCForm.sourceType,
            has_qr: formatCForm.hasQr === 'true',
            qr_displayed:
              formatCForm.hasQr === 'true'
                ? formatCForm.qrDisplayed === 'true'
                : null,
          },
          priority_info: {
            used_manual_input: formatCForm.usedManualInput === 'true',
            frequency: formatCForm.frequency,
            daily_count: parseInt(formatCForm.dailyCount) || 0,
          },
        },
      })
      setFormatCModalOpen(false)
      setFormatCForm(initialFormatCForm)
      showToast(config.shortServiceName + 'への報告が完了しました。', 'success')
    } catch (e) {
      console.log(e)
    }
  }

  const shortServiceName = config.shortServiceName

  // Patch
  const inferenceErrorTypes = [
    { type: 'med_shortage', displayLabel: '薬剤に欠損がある' },
    { type: 'med_mistake', displayLabel: '薬剤に誤りがある' },
    { type: 'youhou_mistake', displayLabel: '用法に誤りがある' },
    {
      type: 'chouzai_youryou_mistake',
      displayLabel: '日数・用量に誤りがある',
    },
    {
      type: 'chouzai_youryou_shortage',
      displayLabel: '日数・用量が「0」になっている',
    },
  ]

  const receiptErrorTypes = [
    {
      type: '再来患者が新患と認識される',
      displayLabel: '患者の取り込みについて',
      description: '再来の患者が新患と取り込まれる場合',
      route: '/help/patient_matching',
      helpContentKey: 'patient_matching' as HelpContentKey,
    },
    {
      type: '医師名がレセコン側の医師名と紐づかない',
      displayLabel: '医師名の取り込みについて',
      description: '医師名がレセコン側の医師名と紐づかない場合',
      route: '/help/doctor_matching',
      helpContentKey: 'doctor_matching' as HelpContentKey,
    },
    {
      type: '用法がうまくレセコンに入らない',
      displayLabel: '用法の取り込みについて',
      description: '用法がレセコンに取り込まれない場合',
      route: '/help/youhou_matching',
      helpContentKey: 'youhou_matching' as HelpContentKey,
    },
  ]

  const mamorukunNotWorkingTypes = [
    {
      type: 'QuickSyncが動いているかの確認',
      displayLabel: 'QuickSyncが動いているかの確認方法',
      description: 'QuickSyncの動作確認について',
      route: '/help/quicksync_check',
      helpContentKey: 'quicksync_check' as HelpContentKey,
    },
    {
      type: 'ネットワークが繋がっているかの確認',
      displayLabel: 'ネットワークが繋がっているかの確認方法',
      description: 'ネットワーク接続の確認について',
      route: '/help/network_check',
      helpContentKey: 'network_check' as HelpContentKey,
    },
  ]

  const ReportSendingOverlay = isSending && (
    <Stack
      sx={{
        zIndex: 1305,
        position: 'absolute',
        bgcolor: 'rgb(0,0,0, 0.4)',
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      spacing={2}
    >
      <CircularProgress style={{ width: '100px', height: '100px' }} />
      <Typography sx={{ color: '#fff', fontSize: '24px' }}>
        報告送信中...
      </Typography>
    </Stack>
  )

  const renderSnackBar = () => (
    <div>
      <Modal
        open={isOtherReportModalOpen}
        onClose={() => setIsOtherReportModalOpen(false)}
      >
        <MuiAlert
          elevation={10}
          variant="filled"
          iconMapping={{
            success: (
              <CampaignRoundedIcon style={{ fontSize: 40, color: 'black' }} />
            ),
          }}
          sx={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '24px',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            width: 600,
          }}
          style={{
            backgroundColor: 'white',
            color: 'black',
          }}
        >
          <IconButton
            aria-label="close"
            color="inherit"
            onClick={() => setIsOtherReportModalOpen(false)}
            style={{ position: 'absolute', top: '0', right: '0' }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
          <Stack direction="column" alignItems="center">
            <Typography variant="h5" sx={{ pb: 2 }}>
              {'その他' + shortServiceName + 'への報告'}
            </Typography>
            <Stack>
              {inferenceErrorTypes.map((item) => (
                <Stack key={item.type} p={1}>
                  <CustomButton
                    variant="contained"
                    sx={{ width: 400, height: 50 }}
                    onClick={() => reportSpecificError(item.type)}
                  >
                    {item.displayLabel}
                  </CustomButton>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </MuiAlert>
      </Modal>
      <Modal open={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: 4, pb: 2 }}>
            <IconButton
              aria-label="close"
              onClick={() => setIsHelpModalOpen(false)}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
            <Stack direction="row" alignItems="center" spacing={1}>
              <HelpOutlineIcon sx={{ fontSize: 28, color: '#000000' }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                }}
              >
                よくあるご質問
              </Typography>
            </Stack>
          </Box>
          <Divider />
          <Box sx={{ p: 4, pt: 3, overflowY: 'auto', flex: 1 }}>
            <Stack spacing={3}>
              {/* まもる君が動かない */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <ReportProblemOutlinedIcon
                    sx={{ fontSize: 24, color: theme.palette.grey[600] }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    まもる君が動かない
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {mamorukunNotWorkingTypes.map((item) => (
                    <Box key={item.type}>
                      <CustomButton
                        variant="outlined"
                        sx={{
                          width: '100%',
                          minHeight: 80,
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          textAlign: 'left',
                          px: 3,
                          py: 2,
                          borderWidth: 2,
                          borderColor: theme.palette.divider,
                          '&:hover': {
                            borderWidth: 2,
                            borderColor: theme.palette.grey[600],
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          setSelectedHelpContent(item.helpContentKey)
                          setIsHelpModalOpen(false)
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 'bold',
                            mb: 0.5,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {item.displayLabel}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </CustomButton>
                    </Box>
                  ))}
                  <Typography
                    variant="body2"
                    sx={{ pl: 2, pt: 1, color: theme.palette.text.secondary }}
                  >
                    ⇒
                    これらが問題ない場合は、スキャナ若しくはレセコン側の問題が考えられます。
                    <br />
                    所定のベンダー様へお問い合わせください。
                  </Typography>
                </Stack>
              </Box>

              <Divider />

              {/* レセコンにスッと情報が入らない */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <IntegrationInstructionsIcon
                    sx={{ fontSize: 24, color: theme.palette.grey[600] }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    レセコンにスッと情報が入らない
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {receiptErrorTypes.map((item) => (
                    <Box key={item.type}>
                      <CustomButton
                        variant="outlined"
                        sx={{
                          width: '100%',
                          minHeight: 80,
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          textAlign: 'left',
                          px: 3,
                          py: 2,
                          borderWidth: 2,
                          borderColor: theme.palette.divider,
                          '&:hover': {
                            borderWidth: 2,
                            borderColor: theme.palette.grey[600],
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          setSelectedHelpContent(item.helpContentKey)
                          setIsHelpModalOpen(false)
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 'bold',
                            mb: 0.5,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {item.displayLabel}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </CustomButton>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Divider />

              {/* その他 */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 24, color: theme.palette.grey[600] }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    その他
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {[
                    {
                      label: '困ったときガイド（別ページへ遷移します）',
                      icon: (
                        <MenuBookIcon sx={{ color: theme.palette.grey[600] }} />
                      ),
                      description: 'うまく動かないときの切り分けと対処',
                      onClick: () => {
                        setIsHelpModalOpen(false)
                        navigate(currentGuidePath())
                      },
                    },
                    {
                      label: '改善依頼',
                      icon: (
                        <BuildIcon sx={{ color: theme.palette.grey[600] }} />
                      ),
                      description: '読み取り結果の改善を依頼',
                      onClick: () => {
                        setIsHelpModalOpen(false)
                        setFormatCModalOpen(true)
                      },
                    },
                    {
                      label: '管理ポータルへ（別ページへ遷移します）',
                      icon: (
                        <DashboardIcon
                          sx={{ color: theme.palette.grey[600] }}
                        />
                      ),
                      description: '各種設定や統計情報を確認',
                      onClick: () => {
                        setIsHelpModalOpen(false)
                        navigate('/portal')
                      },
                    },
                  ].map((item) => (
                    <Box key={item.label}>
                      <CustomButton
                        variant="outlined"
                        sx={{
                          width: '100%',
                          minHeight: 80,
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          textAlign: 'left',
                          px: 3,
                          py: 2,
                          borderWidth: 2,
                          borderColor: theme.palette.divider,
                          '&:hover': {
                            borderWidth: 2,
                            borderColor: theme.palette.grey[600],
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={item.onClick}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{ width: '100%' }}
                        >
                          {item.icon}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 'bold',
                                mb: 0.5,
                                color: theme.palette.text.primary,
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                              }}
                            >
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </CustomButton>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Modal>

      {/* フォーマットC 不具合報告モーダル */}
      <Modal open={formatCModalOpen} onClose={() => setFormatCModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: 3, pb: 2 }}>
            <IconButton
              aria-label="close"
              onClick={() => setFormatCModalOpen(false)}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
            <Stack direction="row" alignItems="center" spacing={1}>
              <BuildIcon sx={{ fontSize: 28, color: '#000000' }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                }}
              >
                改善依頼
              </Typography>
            </Stack>
          </Box>
          <Divider />

          <Box
            sx={{
              p: 3,
              overflowY: 'auto',
              flex: 1,
              '& .MuiInputLabel-asterisk': {
                color: theme.palette.warning.main,
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 2, color: theme.palette.text.secondary }}
            >
              <span style={{ color: theme.palette.warning.main }}>*</span>{' '}
              は必須項目です
            </Typography>
            <Stack spacing={3}>
              {/* 基本情報（自動入力・表示のみ） */}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', mb: 1 }}
                >
                  基本情報（自動入力）
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'grey.50',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      送信日時: {dayjs().format('YYYY/MM/DD HH:mm')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ユーザID:{' '}
                      {(authData?.data?.user as { email?: string })?.email ||
                        '（自動取得）'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      リクエストID: {prescription?.id || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      店舗名: {pharmacyName || '—'}
                    </Typography>
                  </Stack>
                </Box>
              </Box>

              <Divider />

              {/* 事象詳細 */}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', mb: 2 }}
                >
                  事象詳細
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    select
                    label="対象の読み取り項目"
                    required
                    fullWidth
                    value={formatCForm.targetItem}
                    onChange={(e) =>
                      updateFormatCForm('targetItem', e.target.value)
                    }
                  >
                    <MenuItem value="医薬品名">医薬品名</MenuItem>
                    <MenuItem value="用法">用法</MenuItem>
                    <MenuItem value="用量">用量</MenuItem>
                    <MenuItem value="氏名">氏名</MenuItem>
                    <MenuItem value="保険">保険</MenuItem>
                    <MenuItem value="コメント">コメント</MenuItem>
                    <MenuItem value="その他">その他</MenuItem>
                  </TextField>
                  <TextField
                    label="処方箋の記載内容"
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    value={formatCForm.correctValue}
                    onChange={(e) =>
                      updateFormatCForm('correctValue', e.target.value)
                    }
                  />
                  <TextField
                    label="希望する読み取り結果"
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="処方箋記載通りの場合は入力不要です"
                    value={formatCForm.desiredResult}
                    onChange={(e) =>
                      updateFormatCForm('desiredResult', e.target.value)
                    }
                  />
                  <TextField
                    label="レセコン表示の読取結果"
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    value={formatCForm.rececomResult}
                    onChange={(e) =>
                      updateFormatCForm('rececomResult', e.target.value)
                    }
                  />
                  <TextField
                    label="webアプリ表示の読取結果"
                    fullWidth
                    multiline
                    minRows={2}
                    value={formatCForm.webAppResult}
                    onChange={(e) =>
                      updateFormatCForm('webAppResult', e.target.value)
                    }
                  />
                  <TextField
                    select
                    label="処方箋原稿の種類"
                    required
                    fullWidth
                    value={formatCForm.sourceType}
                    onChange={(e) =>
                      updateFormatCForm('sourceType', e.target.value)
                    }
                  >
                    <MenuItem value="原本">原本</MenuItem>
                    <MenuItem value="FAX">FAX</MenuItem>
                    <MenuItem value="調剤予約アプリ送信画像">
                      調剤予約アプリ送信画像
                    </MenuItem>
                  </TextField>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      QRコード{' '}
                      <span style={{ color: theme.palette.warning.main }}>
                        *
                      </span>
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, color: theme.palette.text.secondary }}
                    >
                      処方箋（原稿）にQRコードが印字されているかをご確認ください
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      value={formatCForm.hasQr}
                      onChange={(_, value) => {
                        if (value !== null) updateFormatCForm('hasQr', value)
                      }}
                    >
                      <ToggleButton value="true">あり</ToggleButton>
                      <ToggleButton value="false">なし</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  {formatCForm.hasQr === 'true' && (
                    <Box>
                      <TextField
                        select
                        label="[QR]表示"
                        required
                        fullWidth
                        value={formatCForm.qrDisplayed}
                        onChange={(e) =>
                          updateFormatCForm('qrDisplayed', e.target.value)
                        }
                        helperText="webアプリの処方せん一覧で、処方箋名の先頭に「[QR]」が表示されているかをご選択ください"
                      >
                        <MenuItem value="true">あり</MenuItem>
                        <MenuItem value="false">なし</MenuItem>
                      </TextField>
                    </Box>
                  )}
                </Stack>
              </Box>

              <Divider />

              {/* 優先度情報 */}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', mb: 2 }}
                >
                  優先度情報
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, color: theme.palette.text.secondary }}
                    >
                      読取処方せん一覧に表示されるまもる君の読取結果の修正が大変なため、読取結果を使わず一から手入力しましたか？
                    </Typography>
                    <TextField
                      select
                      label="手入力有無"
                      required
                      fullWidth
                      value={formatCForm.usedManualInput}
                      onChange={(e) =>
                        updateFormatCForm('usedManualInput', e.target.value)
                      }
                    >
                      <MenuItem value="true">はい</MenuItem>
                      <MenuItem value="false">いいえ</MenuItem>
                    </TextField>
                  </Box>
                  <Divider />
                  <TextField
                    select
                    label="不具合の発生頻度"
                    required
                    fullWidth
                    value={formatCForm.frequency}
                    onChange={(e) =>
                      updateFormatCForm('frequency', e.target.value)
                    }
                  >
                    <MenuItem value="毎日">毎日</MenuItem>
                    <MenuItem value="週に数回">週に数回</MenuItem>
                    <MenuItem value="週に1回">週に1回</MenuItem>
                    <MenuItem value="月に1回">月に1回</MenuItem>
                  </TextField>
                  <TextField
                    label="1日あたりの発生回数"
                    type="number"
                    required
                    fullWidth
                    value={formatCForm.dailyCount}
                    onChange={(e) =>
                      updateFormatCForm('dailyCount', e.target.value)
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">回</InputAdornment>
                      ),
                    }}
                    inputProps={{ min: 0 }}
                  />
                </Stack>
              </Box>
            </Stack>
          </Box>

          <Divider />
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setFormatCModalOpen(false)
                setIsHelpModalOpen(true)
              }}
              sx={{
                borderWidth: 2,
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                '&:hover': {
                  borderWidth: 2,
                  borderColor: theme.palette.grey[600],
                },
              }}
            >
              よくある質問に戻る
            </Button>
            <Button
              variant="contained"
              onClick={handleFormatCSubmit}
              disabled={!isFormatCFormValid || isSending}
              endIcon={<SendIcon />}
              sx={{
                backgroundColor: theme.palette.primary.dark,
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#1a237e',
                },
              }}
            >
              送信
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  )

  // Patch
  const openModal = useCallback(() => {
    if (config.features.helpModal) {
      setIsHelpModalOpen(true)
    } else {
      setIsOtherReportModalOpen(true)
    }
  }, [config.features.helpModal])

  const renderHelpContentModal = selectedHelpContent && (
    <HelpContentModal
      open={!!selectedHelpContent}
      onClose={() => setSelectedHelpContent(null)}
      title={helpContents[selectedHelpContent].title}
      markdownContent={helpContents[selectedHelpContent].content}
      onBack={() => {
        setSelectedHelpContent(null)
        setIsHelpModalOpen(true)
      }}
    />
  )

  return {
    reportBasicResult,
    reportRPUnit,
    reportMedication,
    reportRPIndex,
    openModal,
    renderSnackBar,
    ReportSendingOverlay,
    renderHelpContentModal,
  }
}
